// ServicesPage.jsx
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaPhoneAlt, FaLine } from "react-icons/fa";

/* ============ THEME ============ */
const THEME = {
  fontFamily:
    "'Inter', 'Sarabun', system-ui, -apple-system, Segoe UI, Roboto, 'Helvetica Neue', Arial, 'Noto Sans Thai', sans-serif",
  pageBg: "#f8fafc",
  textOnLight: "#1e293b",
  textMuted: "#64748b",
  p1: "#2563eb", // Primary Blue
  p2: "#d4af37", // Gold
};

const CONTACT_INFO = {
  phone: "02-276-9968",
  line: "https://line.me/R/ti/p/@443namoj",
};

const HERO_MEDIA = {
  video: "https://cdn.coverr.co/videos/coverr-reading-a-book-1261/1080p.mp4",
  image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?q=80&w=2070&auto=format&fit=crop",
};

// หมวดบริการ (เรียงตามลำดับที่จะแสดง) — label ดึงจาก services_page.categories.*
const CATEGORY_DEFS = [
  { key: "tax_acc" },
  { key: "corporate" },
  { key: "litigation" },
  { key: "family" },
  { key: "foreign" },
  { key: "tech" },
];

/* ============ SUB-COMPONENTS ============ */

function ContactSection({ isSpecial = false }) {
  const { t } = useTranslation();
  const accent = "#D4AF37";
  const btnText = "#fff";

  return (
    <div style={{ marginTop: "14px", padding: "14px", border: `1px solid rgba(212,175,55,0.3)`, borderRadius: "12px", background: "#fff", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
      <p style={{ margin: "0 0 10px", fontSize: "12px", fontWeight: "700", color: "#1e293b", textAlign: "center", textTransform: "uppercase", letterSpacing: "1px" }}>
        {t("services_page.consult_now")}
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <div
          style={{
            display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
            padding: "10px 16px", borderRadius: "8px", backgroundColor: "#0b1220", color: "#fff",
            fontSize: "14px", fontWeight: "700",
          }}
        >
          <FaPhoneAlt size={12} /> <span>{CONTACT_INFO.phone}</span>
        </div>
        <a
          href={CONTACT_INFO.line}
          target="_blank" rel="noopener noreferrer"
          style={{
            display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
            padding: "10px 16px", borderRadius: "8px", backgroundColor: "#00c300", color: "#fff",
            textDecoration: "none", fontSize: "14px", fontWeight: "700", transition: "all 0.3s ease"
          }}
        >
          <FaLine size={16} /> {t("services_page.line_chat")}
        </a>
      </div>
    </div>
  );
}

function ServiceCard({ service, t }) {
  return (
    <div id={service.id} className="service-card reveal">
      <div className="card-media">
        <img src={service.image} alt={service.title} />
        <div className="card-badge">{t(`services_page.badges.${service.badge || "legal"}`)}</div>
      </div>
      <div className="card-content">
        <h3 className="card-title">{service.title}</h3>
        <p className="card-intro">{service.intro}</p>
        
        <ul className="card-features">
          {Array.isArray(service.bullets) && service.bullets.map((b, i) => (
            <li key={i}>{b}</li>
          ))}
        </ul>

        <div className="card-footer">
          <ContactSection />
        </div>
      </div>
    </div>
  );
}

/* ============ MAIN PAGE ============ */
export default function ServicesPage() {
  const { t } = useTranslation();
  const [activeCat, setActiveCat] = useState("all");
  const contentRef = useRef(null);

  // กดหมวด → เปลี่ยน filter + เลื่อนลงไปยังส่วนการ์ดบริการ (hero สูง 75vh การ์ดอยู่ใต้ fold)
  const handleSelectCat = (key) => {
    setActiveCat(key);
    requestAnimationFrame(() => {
      contentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const SERVICES_LIST = [
    { id: "tax_planning", category: "tax_acc", ...t("services_page.items.tax_planning", { returnObjects: true }), image: "https://images.unsplash.com/photo-1554224154-26032ffc0d07?q=80&w=1600&auto=format&fit=crop", badge: "tax" },
    { id: "tax_litigation", category: "tax_acc", ...t("services_page.items.tax_litigation", { returnObjects: true }), image: "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?q=80&w=1600&auto=format&fit=crop", badge: "tax" },
    { id: "gov_audit", category: "tax_acc", ...t("services_page.items.gov_audit", { returnObjects: true }), image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=1600&auto=format&fit=crop", badge: "accounting" },
    { id: "corporate", category: "corporate", ...t("services_page.items.corporate", { returnObjects: true }), image: "https://images.unsplash.com/photo-1523958203904-cdcb402031fd?q=80&w=1600&auto=format&fit=crop", badge: "legal" },
    { id: "notarial", category: "corporate", ...t("services_page.items.notarial", { returnObjects: true }), image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?q=80&w=1600&auto=format&fit=crop", badge: "legal" },
    { id: "fda_registration", category: "corporate", ...t("services_page.items.fda_registration", { returnObjects: true }), image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?q=80&w=1600&auto=format&fit=crop", badge: "legal" },
    { id: "litigation", category: "litigation", ...t("services_page.items.litigation", { returnObjects: true }), image: "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?q=80&w=1600&auto=format&fit=crop", badge: "legal" },
    { id: "debt", category: "litigation", ...t("services_page.items.debt", { returnObjects: true }), image: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?q=80&w=1600&auto=format&fit=crop", badge: "legal" },
    { id: "asset_enforcement", category: "litigation", ...t("services_page.items.asset_enforcement", { returnObjects: true }), image: "https://images.unsplash.com/photo-1450101499163-c8848c66ca85?q=80&w=1600&auto=format&fit=crop", badge: "legal" },
    { id: "labor", category: "litigation", ...t("services_page.items.labor", { returnObjects: true }), image: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?q=80&w=1600&auto=format&fit=crop", badge: "legal" },
    { id: "criminal_special", category: "litigation", ...t("services_page.items.criminal_special", { returnObjects: true }), image: "https://images.unsplash.com/photo-1505664194779-8beaceb93744?q=80&w=1600&auto=format&fit=crop", badge: "legal" },
    { id: "family", category: "family", ...t("services_page.items.family", { returnObjects: true }), image: "https://images.unsplash.com/photo-1519741497674-611481863552?q=80&w=1600&auto=format&fit=crop", badge: "legal" },
    { id: "valuation", category: "family", ...t("services_page.items.valuation", { returnObjects: true }), image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?q=80&w=1600&auto=format&fit=crop", badge: "legal" },
    { id: "due_diligence", category: "corporate", ...t("services_page.items.due_diligence", { returnObjects: true }), image: "https://images.unsplash.com/photo-1566073771259-6a8506099945?q=80&w=1600&auto=format&fit=crop", badge: "legal" },
    { id: "foreign_compliance", category: "foreign", ...t("services_page.items.foreign_compliance", { returnObjects: true }), image: "https://images.unsplash.com/photo-1521791136064-7986c2920216?q=80&w=1600&auto=format&fit=crop", badge: "legal" },
    { id: "visa_workpermit", category: "foreign", ...t("services_page.items.visa_workpermit", { returnObjects: true }), image: "https://images.unsplash.com/photo-1488646953014-85cb44e25828?q=80&w=1600&auto=format&fit=crop", badge: "legal" },
    { id: "ai_solution", category: "tech", ...t("services_page.items.ai_solution", { returnObjects: true }), image: "https://images.unsplash.com/photo-1518770660439-4636190af475?q=80&w=1600&auto=format&fit=crop", badge: "tech" },
  ];

  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("reveal-in");
            io.unobserve(e.target);
          }
        }),
      { threshold: 0.15 }
    );
    // re-observe เมื่อเปลี่ยนหมวด (การ์ดชุดใหม่ที่เพิ่ง mount ยังไม่ถูกเผย)
    document.querySelectorAll(".reveal:not(.reveal-in)").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [activeCat]);

  // เลื่อนไปยังบริการที่ลิงก์มาจากหน้าแรก (/service#id)
  useEffect(() => {
    const id = window.location.hash.replace("#", "");
    if (!id) return;
    const target = document.getElementById(id);
    if (!target) return;
    const tid = setTimeout(() => {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 350);
    return () => clearTimeout(tid);
  }, []);

  return (
    <div className="services-page-new">
      <Style />

      {/* HERO */}
      <header className="hero">
        <video className="hero-vid" autoPlay muted loop playsInline poster={HERO_MEDIA.image}>
          <source src={HERO_MEDIA.video} type="video/mp4" />
        </video>
        <div className="hero-grad" />
        <div className="hero-inner">
          <h1 className="hero-h1" dangerouslySetInnerHTML={{ __html: t("services_page.hero_title").replace("\n", "<br/>") }} />
          <p className="hero-p" dangerouslySetInnerHTML={{ __html: t("services_page.hero_desc").replace("\n", "<br/>") }} />
          <div className="cat-chips">
            {[{ key: "all" }, ...CATEGORY_DEFS].map((c) => (
              <button
                key={c.key}
                type="button"
                className={`cat-chip ${activeCat === c.key ? "active" : ""}`}
                onClick={() => handleSelectCat(c.key)}
              >
                {t(`services_page.categories.${c.key}`)}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* MAIN CONTENT */}
      <main className="content-container" ref={contentRef}>
        <div className="section-header">
           <h2 className="title-section">{t("services_page.section_title")}</h2>
           <p className="subtitle-section">{t("services_page.section_subtitle")}</p>
        </div>

        {CATEGORY_DEFS
          .filter((cat) => activeCat === "all" || cat.key === activeCat)
          .map((cat) => {
            const items = SERVICES_LIST.filter((s) => s.category === cat.key);
            if (!items.length) return null;
            return (
              <section key={cat.key} className="cat-group">
                <h3 className="cat-group-title">{t(`services_page.categories.${cat.key}`)}</h3>
                <div className="services-grid">
                  {items.map((s) => (
                    <ServiceCard key={s.id} service={s} t={t} />
                  ))}
                </div>
              </section>
            );
          })}
      </main>

      {/* FOOTER CTA */}
      <section className="final-banner reveal">
          <h2>{t("services_page.final_banner.title")}</h2>
          <p>{t("services_page.final_banner.desc")}</p>
          <div className="banner-btns">
            <div style={{ padding: "18px 45px", background: "#0b1220", color: "#fff", borderRadius: "50px", fontSize: "18px", fontWeight: "900", display: "flex", alignItems: "center", gap: "10px", boxShadow: "0 10px 30px rgba(11, 18, 32, 0.2)" }}>
                <FaPhoneAlt /> {CONTACT_INFO.phone}
            </div>
            <a className="btn-line" href={CONTACT_INFO.line} target="_blank" rel="noopener noreferrer">
                <FaLine /> LINE CHAT
            </a>
          </div>
      </section>
    </div>
  );
}

/* ============ STYLING ============ */
function Style() {
  return (
    <style>{`
      .services-page-new { background: #f8fafc; color: #1e293b; scroll-behavior: smooth; font-family: 'Inter', 'Sarabun', sans-serif; }
      
      /* Hero */
      .hero { position: relative; height: 75vh; display: flex; align-items: center; justify-content: center; text-align: center; overflow: hidden; background: #0b1220; }
      .hero-vid { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; z-index: 1; filter: brightness(0.4); }
      .hero-grad { position: absolute; inset: 0; background: linear-gradient(to bottom, rgba(11,21,38,0.85) 0%, rgba(11,21,38,0.4) 60%, #f8fafc 100%); z-index: 2; }
      .hero-inner { position: relative; z-index: 3; max-width: 1000px; padding: 20px; transition: 0.5s ease; }
      .nav-bread { font-size: 13px; font-weight: 700; color: #fff; opacity: 1; margin-bottom: 20px; letter-spacing: 2px; text-shadow: 0 2px 4px rgba(0,0,0,0.5); }
      .nav-bread a { color: #fff; text-decoration: none; }
      .hero-h1 { font-size: clamp(1.9rem, 4.2vw, 3.2rem); font-weight: 900; margin-bottom: 22px; line-height: 1.2; color: #fff; text-shadow: 0 5px 30px rgba(0,0,0,0.5); }
      .hero-p { font-size: clamp(1rem, 1.5vw, 1.3rem); line-height: 1.8; color: #fff; opacity: 1; margin-bottom: 45px; max-width: 850px; margin-left: auto; margin-right: auto; text-shadow: 0 2px 10px rgba(0,0,0,0.5); font-weight: 500; }
      
      .hero-tabs { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; }
      .tab-btn { padding: 14px 28px; background: #0b1220; border: 2px solid rgba(255,255,255,0.2); border-radius: 50px; color: #fff; text-decoration: none; font-size: 14px; font-weight: 700; transition: 0.3s; box-shadow: 0 4px 15px rgba(0,0,0,0.2); }
      .tab-btn:hover { background: #1e293b; transform: translateY(-3px); border-color: #D4AF37; }
      .tab-btn.active { background: #D4AF37; color: #0b1220; border-color: #D4AF37; box-shadow: 0 10px 25px rgba(212, 175, 55, 0.5); }

      /* Category chips (filter) */
      .cat-chips { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; }
      .cat-chip { padding: 12px 28px; background: #0b1220; border: 2px solid rgba(255,255,255,0.2); border-radius: 50px; color: #fff; cursor: pointer; font-family: inherit; font-size: 14px; font-weight: 700; transition: 0.3s; box-shadow: 0 4px 15px rgba(0,0,0,0.2); }
      .cat-chip:hover { background: #1e293b; transform: translateY(-3px); border-color: #D4AF37; }
      .cat-chip.active { background: #D4AF37; color: #0b1220; border-color: #D4AF37; box-shadow: 0 10px 25px rgba(212,175,55,0.5); }

      /* Category group */
      .cat-group { margin-bottom: 64px; }
      .cat-group:last-child { margin-bottom: 0; }
      .cat-group-title { font-size: clamp(1.4rem, 2.4vw, 1.95rem); font-weight: 900; color: #0b1220; margin: 0 0 28px; padding-left: 16px; border-left: 5px solid #D4AF37; line-height: 1.2; }

      /* Mini Nav */
      .fixed-nav { display: none; position: fixed; right: 30px; top: 50%; transform: translateY(-50%); z-index: 1000; border-right: 2px solid rgba(11, 18, 32, 0.1); padding-right: 20px; flex-direction: column; gap: 12px; }
      .label-top { font-size: 11px; font-weight: 800; color: #0b1220; text-align: right; margin-bottom: 8px; letter-spacing: 2px; text-transform: uppercase; }
      .fixed-nav a { text-decoration: none; color: #475569; font-size: 14px; font-weight: 600; text-align: right; transition: 0.3s; }
      .fixed-nav a:hover { color: #0b1220; transform: translateX(-3px); }
      .fixed-nav a.active { color: #D4AF37; font-size: 15px; font-weight: 900; transform: translateX(-5px); text-shadow: 0 0 1px rgba(212, 175, 55, 0.3); }

      /* Section Layout */
      .content-container { max-width: 1300px; margin: 0 auto; padding: 60px 24px; scroll-margin-top: 90px; }
      .section-header { text-align: center; margin-bottom: 44px; }
      .title-section { font-size: clamp(2rem, 3.4vw, 2.8rem); font-weight: 900; color: #0b1220; margin-bottom: 14px; letter-spacing: -0.02em; }
      .subtitle-section { font-size: 1.1rem; color: #64748b; max-width: 700px; margin: 0 auto; line-height: 1.55; }

      /* Services Grid */
      .services-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 80px 48px; justify-items: stretch; }

      /* Service Card */
      .services-page-new .service-card { width: 100%; max-width: none; min-height: 0; background: #fff; border-radius: 20px; overflow: hidden; box-shadow: 0 8px 30px rgba(0,0,0,0.12); border: 1px solid rgba(0,0,0,0.08); transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1); display: flex; flex-direction: column; }
      .services-page-new .service-card:hover { transform: translateY(-15px); box-shadow: 0 20px 50px rgba(11,18,32,0.15); border-color: rgba(212, 175, 55, 0.2); }
      
      .card-media { position: relative; height: 270px; overflow: hidden; }
      .card-media img { width: 100%; height: 100%; object-fit: cover; transition: 1s; }
      .service-card:hover .card-media img { transform: scale(1.1); }
      .card-badge { position: absolute; top: 14px; left: 14px; background: rgba(11,18,32,0.8); color: #fff; padding: 5px 12px; border-radius: 50px; font-size: 10px; font-weight: 800; letter-spacing: 1px; backdrop-filter: blur(5px); }

      .card-content { padding: 28px; display: flex; flex-direction: column; flex-grow: 1; }
      .card-title { font-size: 1.5rem; font-weight: 900; color: #0b1220; margin-bottom: 10px; }
      .card-intro { font-size: 0.95rem; color: #64748b; line-height: 1.55; margin-bottom: 14px; min-height: 0; }

      .card-features { list-style: none; padding: 0; margin: 0 0 16px; flex-grow: 1; }
      .card-features li { position: relative; padding-left: 24px; margin-bottom: 8px; font-size: 0.92rem; color: #334155; line-height: 1.45; font-weight: 500; }
      .card-features li::before { content: "✓"; position: absolute; left: 0; color: #D4AF37; font-weight: 900; font-size: 0.95rem; }

      .quote-box { background: #f8fafc; border: 1px dashed #cbd5e1; padding: 15px 20px; border-radius: 12px; margin-bottom: 20px; display: flex; justify-content: space-between; align-items: center; }
      .quote-label { font-size: 12px; font-weight: 700; color: #64748b; text-transform: uppercase; }
      .quote-value { font-size: 1.4rem; font-weight: 900; color: #0b1220; }

      /* Final Banner */
      .final-banner { padding: 120px 20px; text-align: center; background: #0b1220; color: #fff; border-radius: 60px 60px 0 0; margin-top: 50px; }
      .final-banner h2 { font-size: clamp(2rem, 5vw, 3.5rem); font-weight: 900; margin-bottom: 25px; }
      .final-banner p { font-size: 1.2rem; opacity: 0.8; max-width: 800px; margin: 0 auto; }
      .banner-btns { display: flex; gap: 20px; justify-content: center; margin-top: 50px; flex-wrap: wrap; }
      .btn-line { padding: 18px 45px; background: #00c300; color: #fff; border-radius: 50px; font-size: 18px; font-weight: 900; text-decoration: none; transition: 0.3s; box-shadow: 0 10px 30px rgba(0, 195, 0, 0.2); display: flex; align-items: center; gap: 10px; }
      .btn-line:hover { transform: translateY(-3px); box-shadow: 0 15px 40px rgba(0, 195, 0, 0.4); }

      /* Animations */
      .reveal { opacity: 0; transform: translateY(30px); transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1); }
      .reveal-in { opacity: 1; transform: translateY(0); }

      @media (max-width: 900px) {
        .fixed-nav { display: none; }
        .hero { height: 60vh; }
        .hero-inner { padding: 40px 20px; }
        .services-grid { grid-template-columns: 1fr; gap: 36px; }
        .content-container { padding: 40px 20px; }
        .card-media { height: 210px; }
        .card-content { padding: 22px; }
        .card-title { font-size: 1.35rem; }
        .card-intro { font-size: 0.92rem; }
        .card-features li { font-size: 0.9rem; }
      }
    `}</style>
  );
}
